import {Suspense, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import PrintableTableGrid from "../../components/basics/table-grid/printable-table-grid/PrintableTableGrid";
import SQLChartWrapper from "../../components/sql-chart/SQLChartWrapper";
import SQLGaugeAndNumberWrapper from "../../components/sql-chart/SQLGaugeAndNumberWrapper";
import {useDatabaseInformation} from "../../context/DatabaseInformationContext";
import {useDashboardDetails} from "../../hooks/useDashboardDetails";
import {createKeyToValueDictionary} from "../../utils/Utils";

function DashboardPrintChart({
    element,
    keysDictionary,
    filterValue,
    filterType,
    dashboardID,
}) {
    const {Filter, Options, Parameters, Type, Group, ReferenceLines, Name, ID} =
        element;

    if (element.Type === "Gauge" || element.Type === "Number") {
        return (
            <div className="break-inside-avoid">
                <div className="header-space" />
                <SQLGaugeAndNumberWrapper
                    Filter={Filter}
                    Options={Options}
                    Parameters={Parameters}
                    Type={Type}
                    keysDictionary={keysDictionary}
                    filterValue={filterValue}
                    dashboardID={dashboardID}
                    itemID={ID}
                />
            </div>
        );
    }
    console.error("CHART", filterType, filterValue);
    return (
        <div className="break-inside-avoid">
            <div className="header-space" />
            {/*<h3 className="justify-content-center">{Name} - {t("chart")}</h3>*/}
            <h3 className="justify-content-center">{Name}</h3>
            <div
                style={{
                    width: 1000,
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "5rem",
                    marginTop: "2rem",
                }}>
                <SQLChartWrapper
                    Filter={Filter}
                    Options={Options}
                    Parameters={Parameters}
                    ReferenceLines={ReferenceLines}
                    group={Group}
                    type={Type}
                    keysDictionary={keysDictionary}
                    filterValue={filterValue}
                    print
                    filterType={filterType}
                    dashboardID={dashboardID}
                    itemID={ID}
                />
            </div>
        </div>
    );
}

function DashboardPrintDetailsRenderer({resource, headers}) {
    const data = resource.read();

    const createWrappedValueFormatter = useCallback((header) => {
        if (header.component) {
            return {
                component: ({value}) => {
                    return header.component(value);
                },
            };
        }
        if (header.valueFormatter) {
            return {
                valueFormatter: (props) => {
                    if (!props) return null;
                    const {value} = props;
                    return header.valueFormatter(value);
                },
            };
        }
        return {};
    }, []);

    const wrappedHeaders = useMemo(() => {
        return headers.map((header) => ({
            ...header,
            ...createWrappedValueFormatter(header),
        }));
    }, [headers, createWrappedValueFormatter]);

    return (
        <PrintableTableGrid data={data} headers={wrappedHeaders} shouldIndex />
    );
}

function DashboardPrintDetails({element, keysDictionary, filterValue}) {
    const {t} = useTranslation();

    const {resource, headers} = useDashboardDetails(
        element,
        filterValue,
        undefined,
        keysDictionary
    );

    const flattenHeaders = useMemo(() => {
        const tmp = [];

        function insertChildren(header) {
            if (header.children) {
                for (let child of header.children) {
                    insertChildren(child);
                }
            } else {
                tmp.push(header);
            }
        }

        for (let header of headers) {
            insertChildren(header);
        }
        return tmp;
    }, [headers]);

    return (
        <div>
            <h3 className="justify-content-center">
                {element.Name} - {t("modals.feedingSowInfoModal.details")}
            </h3>
            <Suspense fallback={<div data-wait="true" />}>
                <DashboardPrintDetailsRenderer
                    resource={resource}
                    headers={flattenHeaders}
                />
            </Suspense>
        </div>
    );
}

function DashboardPrintItem({
    element,
    type,
    keysDictionary,
    filterValue,
    filterType,
    dashboardID,
}) {
    if (type === "Chart")
        return (
            <DashboardPrintChart
                element={element}
                keysDictionary={keysDictionary}
                filterValue={filterValue}
                filterType={filterType}
                dashboardID={dashboardID}
            />
        );
    return (
        <DashboardPrintDetails
            element={element}
            keysDictionary={keysDictionary}
            filterValue={filterValue}
        />
    );
}

export default function DashboardPrint({children, resource}) {
    const {schema: keysData} = useDatabaseInformation();

    const {printColumns, dashboard, filterValue} = useMemo(() => {
        const [dashboardList] = children;
        console.log("DASHBOARD", dashboardList.props);
        return {
            printColumns: dashboardList.props.printData,
            dashboard: dashboardList.props.dashboard,
            filterValue: dashboardList.props.filterValue,
        };
    }, [children]);

    const elements = useMemo(() => {
        const tmp = [];
        const dashboardElements = createKeyToValueDictionary(
            dashboard.SetData.Items,
            "ID"
        );
        for (let row of printColumns) {
            if (row.type) {
                const [id, type] = row.type.split("_");
                tmp.push({type, element: dashboardElements[id]});
            }
        }
        return tmp;
    }, [printColumns, dashboard]);

    const keys = useMemo(() => {
        return keysData.map(({key, type}) => ({
            parameter: key,
            type,
        }));
    }, [keysData]);

    const keysDictionary = useMemo(
        () => createKeyToValueDictionary(keys, "parameter"),
        [keys]
    );

    return elements.map((row, index) => (
        <DashboardPrintItem
            key={index}
            {...row}
            keysDictionary={keysDictionary}
            filterValue={filterValue}
            filterType={dashboard.SetData.FilterType}
            dashboardID={dashboard.SetID}
        />
    ));
}
